import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Search, MapPin, ShoppingBag, Download, Eye, EyeOff, CreditCard, CheckCircle, 
  XCircle, Clock, Star, Phone, Mail, Euro, Calendar, FileText, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl } from '../../utils/images';

const verificationColors = {
  'En attente': 'bg-orange-100 text-orange-700',
  'Vérifié': 'bg-emerald-100 text-emerald-700',
  'Rejeté': 'bg-red-100 text-red-700',
};

export default function AdminRestaurants() {
  useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [restaurantDocs, setRestaurantDocs] = useState([]);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showIban, setShowIban] = useState(false);

  const fetchRestaurants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/restaurants`);
      setRestaurants(response.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  const handleToggleStatus = async (restaurantId) => {
    try {
      await axios.put(`${API}/admin/restaurants/${restaurantId}/toggle`, {});
      toast.success('Statut mis à jour');
      fetchRestaurants();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleBulkToggle = async (activate) => {
    if (selectedIds.length === 0) {
      toast.error('Sélectionnez au moins un restaurant');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/restaurants/bulk-toggle`, {
        restaurant_ids: selectedIds,
        activate
      });
      toast.success(`${selectedIds.length} restaurant(s) ${activate ? 'activé(s)' : 'désactivé(s)'}`);
      setSelectedIds([]);
      fetchRestaurants();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/restaurants`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'restaurants_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export téléchargé');
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };

  const openDetailModal = async (restaurant) => {
    try {
      const [detailRes, docsRes] = await Promise.all([
        axios.get(`${API}/admin/restaurants/${restaurant.id}/details`),
        axios.get(`${API}/documents/admin/restaurant/${restaurant.id}`),
      ]);
      setSelectedRestaurant(detailRes.data);
      setRestaurantDocs(docsRes.data.documents || []);
      setShowIban(false);
      setShowDetailModal(true);
    } catch {
      toast.error('Erreur lors du chargement des details');
    }
  };

  const openVerifyModal = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setRejectionReason('');
    setShowVerifyModal(true);
  };

  const handleVerify = async (status) => {
    if (!selectedRestaurant) return;
    setVerifying(true);
    
    try {
      await axios.put(`${API}/admin/restaurants/${selectedRestaurant.id}/verify`, {
        verification_status: status,
        rejection_reason: status === 'Rejeté' ? rejectionReason : null
      });
      
      toast.success(status === 'Vérifié' ? 'Restaurant vérifié' : 'Restaurant rejeté');
      setShowVerifyModal(false);
      fetchRestaurants();
    } catch {
      toast.error('Erreur lors de la vérification');
    } finally {
      setVerifying(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRestaurants.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRestaurants.map(r => r.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          r.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                          (statusFilter === 'active' && r.is_active) ||
                          (statusFilter === 'inactive' && !r.is_active);
    const matchesVerification = verificationFilter === 'all' ||
                                r.verification_status === verificationFilter;
    return matchesSearch && matchesStatus && matchesVerification;
  });

  const pendingCount = restaurants.filter(r => r.verification_status === 'En attente').length;

  return (
    <AdminLayout title="Gestion des restaurants">
      {/* Pending Verification Alert */}
      {pendingCount > 0 && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-orange-500" />
            <span className="text-orange-700 font-medium">
              {pendingCount} restaurant(s) en attente de vérification
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setVerificationFilter('En attente')}
            className="border-orange-300 text-orange-700 hover:bg-orange-100"
          >
            Voir
          </Button>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un restaurant..."
              className="pl-12 rounded-xl"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 rounded-xl">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="active">Actifs</SelectItem>
              <SelectItem value="inactive">Inactifs</SelectItem>
            </SelectContent>
          </Select>
          <Select value={verificationFilter} onValueChange={setVerificationFilter}>
            <SelectTrigger className="w-full sm:w-44 rounded-xl">
              <SelectValue placeholder="Vérification" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="En attente">En attente</SelectItem>
              <SelectItem value="Vérifié">Vérifié</SelectItem>
              <SelectItem value="Rejeté">Rejeté</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleBulkToggle(true)}
                className="text-emerald-600 border-emerald-300"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Activer ({selectedIds.length})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleBulkToggle(false)}
                className="text-red-600 border-red-300"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Désactiver
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExport}
          >
            <Download className="w-4 h-4 mr-1" />
            Exporter
          </Button>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucun restaurant trouvé</div>
        ) : (
          filteredRestaurants.map((restaurant) => (
            <div key={restaurant.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start gap-3 mb-3">
                <Checkbox
                  checked={selectedIds.includes(restaurant.id)}
                  onCheckedChange={() => toggleSelect(restaurant.id)}
                />
                {restaurant.logo ? (
                  <img src={getFullImageUrl(restaurant.logo)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gray-100" />
                )}
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <span className="font-semibold text-gray-900">{restaurant.name}</span>
                    <div className="flex gap-1">
                      <Badge className={restaurant.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {restaurant.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-sm text-gray-600">
                    <MapPin className="w-3 h-3" />
                    {restaurant.city}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <Badge className={verificationColors[restaurant.verification_status || 'Vérifié']}>
                  {restaurant.verification_status || 'Vérifié'}
                </Badge>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openDetailModal(restaurant)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  {restaurant.verification_status === 'En attente' && (
                    <Button variant="ghost" size="sm" onClick={() => openVerifyModal(restaurant)}>
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    </Button>
                  )}
                  <Switch
                    checked={restaurant.is_active}
                    onCheckedChange={() => handleToggleStatus(restaurant.id)}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucun restaurant trouvé</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4">
                    <Checkbox
                      checked={selectedIds.length === filteredRestaurants.length && filteredRestaurants.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-gray-600">Restaurant</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-gray-600">Ville</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-gray-600">Vérification</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-gray-600">Commandes</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-gray-600">Statut</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRestaurants.map((restaurant) => (
                  <tr key={restaurant.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <Checkbox
                        checked={selectedIds.includes(restaurant.id)}
                        onCheckedChange={() => toggleSelect(restaurant.id)}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {restaurant.logo ? (
                          <img src={getFullImageUrl(restaurant.logo)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100" />
                        )}
                        <div>
                          <span className="font-semibold text-gray-900">{restaurant.name}</span>
                          <p className="text-xs text-gray-500">{restaurant.owner_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="flex items-center gap-1 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {restaurant.city}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={verificationColors[restaurant.verification_status || 'Vérifié']}>
                        {restaurant.verification_status || 'Vérifié'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <span className="flex items-center gap-1 text-gray-600">
                        <ShoppingBag className="w-4 h-4" />
                        {restaurant.order_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={restaurant.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {restaurant.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openDetailModal(restaurant)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {restaurant.verification_status === 'En attente' && (
                          <Button variant="ghost" size="sm" onClick={() => openVerifyModal(restaurant)}>
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          </Button>
                        )}
                        <Switch
                          checked={restaurant.is_active}
                          onCheckedChange={() => handleToggleStatus(restaurant.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du restaurant</DialogTitle>
          </DialogHeader>
          
          {selectedRestaurant && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start gap-4">
                {selectedRestaurant.logo ? (
                  <img src={getFullImageUrl(selectedRestaurant.logo)} alt="" className="w-20 h-20 rounded-xl object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-gray-100" />
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedRestaurant.name}</h2>
                  <p className="text-gray-500">{selectedRestaurant.cuisine_type?.join(', ')}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="font-medium">{selectedRestaurant.avg_rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-gray-400">({selectedRestaurant.total_reviews || 0} avis)</span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{selectedRestaurant.address}, {selectedRestaurant.city}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{selectedRestaurant.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{selectedRestaurant.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">
                    Créé le {selectedRestaurant.created_at && new Date(selectedRestaurant.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>

              {/* Stats */}
              {selectedRestaurant.stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-emerald-50 rounded-xl">
                    <p className="text-2xl font-bold text-emerald-600">{selectedRestaurant.stats.total_orders}</p>
                    <p className="text-sm text-gray-600">Commandes</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600">{selectedRestaurant.stats.total_revenue?.toFixed(0)} €</p>
                    <p className="text-sm text-gray-600">Revenu total</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-gray-600">{selectedRestaurant.menu_stats?.categories || 0}</p>
                    <p className="text-sm text-gray-600">Catégories</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-gray-600">{selectedRestaurant.menu_stats?.dishes || 0}</p>
                    <p className="text-sm text-gray-600">Plats</p>
                  </div>
                </div>
              )}

              {/* Owner Info */}
              {selectedRestaurant.owner && (
                <div className="p-4 bg-blue-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-3">Propriétaire</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Nom:</span>
                      <p className="font-medium">{selectedRestaurant.owner.name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium">{selectedRestaurant.owner.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Téléphone:</span>
                      <p className="font-medium">{selectedRestaurant.owner.phone || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Inscrit le:</span>
                      <p className="font-medium">
                        {selectedRestaurant.owner.created_at && new Date(selectedRestaurant.owner.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Coordonnées de facturation */}
              {(selectedRestaurant.iban || selectedRestaurant.bic || selectedRestaurant.vat_number) && (
                <div className="p-4 bg-amber-50 rounded-xl" data-testid="admin-restaurant-billing">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-amber-600" />
                    Coordonnées de facturation
                  </h3>
                  <div className="space-y-3 text-sm">
                    {selectedRestaurant.iban && (
                      <div>
                        <span className="text-gray-500">IBAN:</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="font-mono font-medium tracking-wide" data-testid="admin-iban-value">
                            {showIban
                              ? selectedRestaurant.iban
                              : `•••• •••• •••• ${String(selectedRestaurant.iban).replace(/\s/g, '').slice(-4)}`}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-amber-700 hover:text-amber-900"
                            onClick={() => setShowIban(v => !v)}
                            data-testid="admin-toggle-iban"
                          >
                            {showIban ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                            {showIban ? 'Masquer' : 'Afficher'}
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedRestaurant.bic && (
                        <div>
                          <span className="text-gray-500">BIC / SWIFT:</span>
                          <p className="font-mono font-medium">{selectedRestaurant.bic}</p>
                        </div>
                      )}
                      {selectedRestaurant.vat_number && (
                        <div>
                          <span className="text-gray-500">N° TVA:</span>
                          <p className="font-medium">{selectedRestaurant.vat_number}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}


              {/* Documents */}
              <div className="p-4 bg-gray-50 rounded-xl" data-testid="admin-restaurant-docs">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-500" />
                  Documents ({restaurantDocs.length})
                </h3>
                {restaurantDocs.length === 0 ? (
                  <p className="text-sm text-gray-400">Aucun document fourni</p>
                ) : (
                  <div className="space-y-2">
                    {restaurantDocs.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-red-500" />
                          <div>
                            <p className="text-sm font-medium">{doc.category}</p>
                            <p className="text-xs text-gray-400">{doc.original_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={`${BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-emerald-500 hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Voir
                          </a>
                          {doc.status === 'approved' ? (
                            <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Approuve</Badge>
                          ) : doc.status === 'rejected' ? (
                            <Badge className="bg-red-100 text-red-800 text-[10px]">Refuse</Badge>
                          ) : (
                            <div className="flex gap-1">
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.patch(`${API}/documents/admin/${doc.id}/status?status=approved`, {});
                                    setRestaurantDocs(prev => prev.map(d => d.id === doc.id ? {...d, status: 'approved'} : d));
                                    toast.success('Document approuve');
                                  } catch { toast.error('Erreur'); }
                                }}
                                className="p-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-600"
                                title="Approuver"
                              >
                                <CheckCircle className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.patch(`${API}/documents/admin/${doc.id}/status?status=rejected`, {});
                                    setRestaurantDocs(prev => prev.map(d => d.id === doc.id ? {...d, status: 'rejected'} : d));
                                    toast.success('Document refuse');
                                  } catch { toast.error('Erreur'); }
                                }}
                                className="p-1 rounded bg-red-100 hover:bg-red-200 text-red-600"
                                title="Refuser"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify Modal */}
      <Dialog open={showVerifyModal} onOpenChange={setShowVerifyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vérifier le restaurant</DialogTitle>
          </DialogHeader>
          
          {selectedRestaurant && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Voulez-vous approuver ou rejeter <strong>{selectedRestaurant.name}</strong> ?
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison du rejet (optionnel)
                </label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Expliquez pourquoi le restaurant est rejeté..."
                  rows={3}
                  className="rounded-xl"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => handleVerify('Rejeté')}
                  disabled={verifying}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Rejeter
                </Button>
                <Button 
                  className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => handleVerify('Vérifié')}
                  disabled={verifying}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approuver
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
